// Following state for the SPA — a reactive Set<string> of usernames the
// authenticated user follows, plus the toggle actions used everywhere
// FollowButton renders.
//
// Source of truth: hydrated by bootAuth() from /api/auth/me's `following`
// array on app boot, and mutated locally on follow/unfollow with optimistic
// updates + rollback on error. The Set is replaced (not mutated in place)
// on every change so Svelte's reactivity picks up the diff.
//
// Keeping this in its own module — and not nesting it inside auth.ts —
// because callers that only care about follow state (FollowButton,
// leaderboard rows, list pages) shouldn't need to import the user object.

import { writable, get, type Readable } from 'svelte/store';
import { api } from '../api';

const _following = writable<Set<string>>(new Set());

/**
 * Read-only view of the current user's following set. Subscribe with
 * `$following` in components — `$following.has('alice')` is the canonical
 * "do I follow alice?" check.
 */
export const following: Readable<Set<string>> = _following;

/** Replace the entire set — used by bootAuth and clearAuth. */
export function setFollowing(usernames: string[]): void {
  _following.set(new Set(usernames));
}

/** Synchronous read for code paths that can't subscribe. */
export function isFollowing(username: string): boolean {
  return get(_following).has(username);
}

/** Server response shape from POST/DELETE /api/users/{u}/follow. */
export interface FollowToggleResponse {
  is_following: boolean;
  followers_count: number;
  following_count: number;
}

/**
 * Follow `username`. Optimistic — adds to the store before the network
 * round-trip so the FollowButton flips instantly. On error the change is
 * reverted and the error rethrown so callers can toast / log.
 *
 * Returns the server's authoritative state + counts so the caller (e.g.
 * the profile page) can update displayed follower / following counts
 * without a separate refetch.
 */
export async function followUser(username: string): Promise<FollowToggleResponse> {
  _following.update((s) => {
    const next = new Set(s);
    next.add(username);
    return next;
  });
  try {
    return await api<FollowToggleResponse>(
      `/api/users/${encodeURIComponent(username)}/follow`,
      { method: 'POST' },
    );
  } catch (err) {
    _following.update((s) => {
      const next = new Set(s);
      next.delete(username);
      return next;
    });
    throw err;
  }
}

/**
 * Unfollow `username`. Optimistic with rollback, same shape as followUser.
 */
export async function unfollowUser(username: string): Promise<FollowToggleResponse> {
  _following.update((s) => {
    const next = new Set(s);
    next.delete(username);
    return next;
  });
  try {
    return await api<FollowToggleResponse>(
      `/api/users/${encodeURIComponent(username)}/follow`,
      { method: 'DELETE' },
    );
  } catch (err) {
    _following.update((s) => {
      const next = new Set(s);
      next.add(username);
      return next;
    });
    throw err;
  }
}
